// Ticket 116 — who owns a lead / task, ClickUp-style.
//
//   <OwnerAvatar userId name size />      initials circle, colour keyed on user_id
//   <OwnerChip .../>                      avatar (+ optional name); click opens a
//                                         staff popover when `onChange` is given
//   <OwnerSelect value staff onChange />  plain Select for forms / filters
//   useStaff()                            the tenant roster from GET /crm/staff
//   ownerParam(filter, me)                'mine' | 'all' | <user_id>  →  query param
import React, { useEffect, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { api } from '../api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

// Deterministic pastel per user so the same person is the same colour on
// every page. Hue from a tiny string hash; brand navy text for contrast.
const HUES = [212, 262, 330, 20, 150, 45, 190, 290, 100, 350];
export function ownerColor(userId = '') {
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  const hue = HUES[h % HUES.length];
  return { bg: `hsl(${hue} 70% 88%)`, fg: `hsl(${hue} 55% 28%)`, ring: `hsl(${hue} 60% 70%)` };
}

export function initialsOf(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function displayName(m) {
  return m?.name || m?.email || m?.user_id || 'Unassigned';
}

const SIZES = { xs: 'w-5 h-5 text-[9px]', sm: 'w-6 h-6 text-[10px]', md: 'w-8 h-8 text-xs', lg: 'w-10 h-10 text-sm' };

export function OwnerAvatar({ userId, name, size = 'sm', className = '', ...rest }) {
  const c = ownerColor(userId);
  const label = name || 'Unassigned';
  return (
    <span
      title={label}
      aria-label={label}
      className={`inline-flex items-center justify-center rounded-full font-semibold select-none flex-shrink-0 ${SIZES[size] || SIZES.sm} ${className}`}
      style={{ background: c.bg, color: c.fg, boxShadow: `inset 0 0 0 1px ${c.ring}` }}
      {...rest}
    >
      {userId ? initialsOf(label) : '?'}
    </span>
  );
}

let staffCache = null;
let staffPromise = null;
export function useStaff() {
  const [staff, setStaff] = useState(staffCache || []);
  useEffect(() => {
    if (staffCache) { setStaff(staffCache); return; }
    staffPromise = staffPromise || api.get('/crm/staff').then(({ data }) => (data?.items || [])).catch(() => []);
    let alive = true;
    staffPromise.then((items) => { staffCache = items; if (alive) setStaff(items); });
    return () => { alive = false; };
  }, []);
  return staff;
}
export function _resetStaffCache() { staffCache = null; staffPromise = null; }

// Filter value → query param. 'all' means "no owner filter" (server still
// applies the read rule, so a plain LO only ever gets their own rows).
export function ownerParam(filter, me) {
  if (!filter || filter === 'all') return {};
  if (filter === 'mine') return me ? { owner_id: me } : {};
  return { owner_id: filter };
}

// Avatar + optional name. With `onChange`, clicking opens a staff popover
// (search + list) and calls onChange(user_id) — the caller does the POST.
export function OwnerChip({ userId, name, staff = [], onChange, showName = false, size = 'sm', testId, stop = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const label = name || staff.find((m) => m.user_id === userId)?.name || (userId ? undefined : 'Unassigned');
  const body = (
    <span className="inline-flex items-center gap-2 min-w-0">
      <OwnerAvatar userId={userId} name={label} size={size} />
      {showName && <span className="text-sm truncate" style={{ color: '#070B5F' }}>{label || '—'}</span>}
    </span>
  );
  if (!onChange) return <span data-testid={testId}>{body}</span>;
  const list = staff.filter((m) => !q || displayName(m).toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(''); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          title={label ? `Owner: ${label} — click to reassign` : 'Assign an owner'}
          aria-label={label ? `Reassign (owner: ${label})` : 'Assign an owner'}
          onClick={(e) => { if (stop) e.stopPropagation(); }}
          className="inline-flex rounded-full hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-400 transition"
        >
          {body}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" onClick={(e) => { if (stop) e.stopPropagation(); }}>
        <div className="relative mb-2">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team…" className="h-8 pl-8 text-sm" data-testid="owner-search" />
        </div>
        <div className="max-h-64 overflow-y-auto -mx-1">
          {list.length === 0 && <div className="text-xs text-slate-400 px-2 py-3 text-center">No one matches.</div>}
          {list.map((m) => {
            const active = m.user_id === userId;
            return (
              <button
                key={m.user_id}
                type="button"
                data-testid={`owner-option-${m.user_id}`}
                onClick={() => { setOpen(false); setQ(''); if (!active) onChange(m.user_id, m); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm hover:bg-slate-50 ${active ? 'bg-slate-50' : ''}`}
              >
                <OwnerAvatar userId={m.user_id} name={displayName(m)} size="sm" />
                <span className="flex-1 min-w-0">
                  <span className="block truncate" style={{ color: '#070B5F' }}>{displayName(m)}</span>
                  <span className="block text-[10px] uppercase tracking-wider text-slate-400">{(m.role || '').replace('_', ' ')}</span>
                </span>
                {active && <Check size={14} className="text-brand-accent" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Standard Select for forms (New Lead / New Task) and filters.
export function OwnerSelect({ value, staff = [], onChange, placeholder = 'Owner', testId, className = '', extra = [] }) {
  return (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className={className} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {extra.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
        {staff.map((m) => (
          <SelectItem key={m.user_id} value={m.user_id}>
            <span className="inline-flex items-center gap-2"><OwnerAvatar userId={m.user_id} name={displayName(m)} size="xs" />{displayName(m)}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Owner filter: Mine / Everyone / each teammate. "Everyone" and the
// per-person entries only make sense for loans.view_all holders.
export function OwnerFilter({ value, onChange, staff = [], canViewAll, testId, className = 'w-44' }) {
  const extra = canViewAll ? [['all', 'Everyone'], ['mine', 'Mine']] : [['mine', 'Mine']];
  return (
    <OwnerSelect
      value={value}
      onChange={onChange}
      staff={canViewAll ? staff : []}
      extra={extra}
      placeholder="Owner"
      testId={testId}
      className={className}
    />
  );
}

// Group rows by owner_id for the "by owner" view. Order: owners with the
// most rows first, unassigned last.
export function groupByOwner(rows, staff = []) {
  const byId = new Map();
  for (const r of rows) {
    const k = r.owner_id || '';
    if (!byId.has(k)) byId.set(k, { owner_id: k, name: r.owner_name || displayName(staff.find((m) => m.user_id === k)) , rows: [] });
    byId.get(k).rows.push(r);
  }
  return Array.from(byId.values()).sort((a, b) => (a.owner_id === '') - (b.owner_id === '') || b.rows.length - a.rows.length || a.name.localeCompare(b.name));
}
